import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { BookOpen, Grid, List, ArrowUpDown, Type } from "lucide-react";
import { StoryCoverTile } from "@/components/ui/story-cover-tile";
import { formatWordCount } from "@/lib/utils";
import type { CrownDiscoverySource } from "@/lib/analytics/story-analytics";

// ─── Types ───────────────────────────────────────────────

interface BrowseStory {
  id: string;
  title: string;
  slug: string;
  synopsis: string | null;
  coverUrl: string | null;
  chapterCount: number;
  totalWordCount: number;
  authorName: string;
}

// ─── Component ───────────────────────────────────────────

interface StoryGridProps {
  stories: BrowseStory[];
  title?: string;
  source?: CrownDiscoverySource;
  sortLabel?: string;
}

/**
 * The "Novels" grid section for the browse page.
 * Includes view mode toggles, sort controls, empty state, and story cards.
 */
export function StoryGrid(t0) {
  const $ = _c(16);
  const {
    stories,
    title: t1,
    source,
    sortLabel: t2
  } = t0;
  const title = t1 === undefined ? "Novels" : t1;
  const sortLabel = t2 === undefined ? "Latest" : t2;
  let t3;
  if ($[0] !== title) {
    t3 = <h2 className="text-xl md:text-2xl font-heading font-bold text-slate-900 dark:text-white">{title}</h2>;
    $[0] = title;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <button className="p-1.5 rounded-md bg-white dark:bg-primary text-primary dark:text-white shadow-sm" disabled={true} title="Grid view (active)"><Grid className="size-5" /></button>;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex p-1 bg-slate-100 dark:bg-primary/10 rounded-lg">{t4}<button className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:text-primary" disabled={true} title="Coming soon"><List className="size-5" /></button></div>;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ArrowUpDown className="size-4" />;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== sortLabel) {
    t7 = <div className="flex items-center gap-3">{t5}<button className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-primary/10 rounded-lg text-sm font-medium transition-all text-slate-500 dark:text-slate-400 hover:text-slate-800 disabled:opacity-50" disabled={true} title="Coming soon">{t6} Sort by: {sortLabel}</button></div>;
    $[5] = sortLabel;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t3 || $[8] !== t7) {
    t8 = <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">{t3}{t7}</div>;
    $[7] = t3;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== source || $[11] !== stories) {
    t9 = stories.length === 0 ? <div className="flex flex-col items-center justify-center py-24 text-center"><div className="size-20 rounded-full bg-primary/10 flex items-center justify-center mb-6"><BookOpen className="size-10 text-primary/50" /></div><h3 className="text-lg font-heading font-bold text-slate-900 dark:text-white mb-2">No novels yet</h3><p className="text-slate-500 dark:text-slate-400 text-sm max-w-md">Published novels will appear here. Check back soon!</p></div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">{stories.map(story => <Link key={story.id} href={`/story/${story.slug}${source ? `?utm_source=${source}` : ""}`} className="group cursor-pointer"><StoryCoverTile coverUrl={story.coverUrl} title={story.title} sizes="(min-width: 1280px) 20vw, (min-width: 1024px) 25vw, (min-width: 640px) 33vw, 50vw" hoverZoom="lg" className="mb-3"><div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><span className="bg-primary text-white p-3 rounded-full shadow-xl"><BookOpen className="size-6" /></span></div></StoryCoverTile><h4 className="font-bold text-sm text-slate-900 dark:text-white truncate">{story.title}</h4><div className="flex items-center justify-between"><p className="text-[11px] text-slate-600 dark:text-slate-400 truncate">by {story.authorName}</p><div className="flex items-center gap-1 text-[11px] font-medium text-accent whitespace-nowrap ml-1"><BookOpen className="size-3" /> {story.chapterCount}</div></div><div className="flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-400 mt-0.5"><Type className="size-3" /><span>{formatWordCount(story.totalWordCount)} words</span></div>{story.synopsis && <p className="text-[10px] text-slate-600 dark:text-slate-400 mt-1 line-clamp-2 leading-relaxed">{story.synopsis}</p>}</Link>)}</div>;
    $[10] = source;
    $[11] = stories;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8 || $[14] !== t9) {
    t10 = <div>{t8}{t9}</div>;
    $[13] = t8;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}